import React, { useEffect, useState } from "react";

import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";

import { deleteSystem, downloadMetadata, getSystems } from "api/systems.js";

import getLogger from "utils/logger.util.js";
import { getRowFormattedData } from "utils/utils.js";
import { landingPageStrings, SYSTEM_TABLE_COLUMNS, userProfileStrings } from "constants/constantStrings.js";

import AgTable from "components/AgTable";
import BadgeLabel from "components/BadgeLabel";
import CriticalDeleteDialog from "components/CriticalDeleteDialog";
import DupontButton from "components/DupontButton";
import Filter from "components/Filter";
import PageLoading from "components/PageLoading/index.js";
import TableDialog from "components/TableDialog/index.js";

import { DEFAULT_SYSTEM_DATA, SYSTEM_FIELDS } from "./SystemForm.constant.js";
import SystemForm from "./SystemForm.jsx";
import { getSystemsTableColDef } from "./SystemTable.config";

const Systems = () => {
  const Logger = getLogger("Systems");
  const [currentSystem, setCurrentSystem] = useState();
  const [isLoaded, setIsLoaded] = useState(false);
  const [systems, setSystems] = useState([]);
  const [systemIDs, setSystemIDs] = useState([]);
  const [systemNames, setSystemNames] = useState([]);
  const [isDeleteSystemModalOpen, setIsDeleteSystemModalOpen] = useState(false);
  const [deleteSystemData, setDeleteSystemData] = useState({});
  const [isRerendering, setIsRerendering] = useState(false);
  const [popupData, setPopupData] = useState();
  const [editSystemIndex, setEditSystemIndex] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [showTableFilterIcon, setShowTableFilterIcon] = useState(false);
  const TABLE_COL_DEF = getSystemsTableColDef(showTableFilterIcon);
  const [systemCount, setSystemCount] = useState("");
  const [openedSysForm, setOpenedSysForm] = useState(null);
  const [baselineDialog, setBaselineDialog] = useState(false);
  const handleOpenSystemForm = () => setOpenedSysForm("Add");
  const [isDeleting, setIsDeleting] = useState(false);
  const objAppInsights = useAppInsightsHandler();

  useEffect(() => {
    if (openedSysForm === "Add") {
      setCurrentSystem(DEFAULT_SYSTEM_DATA);
    } else if (openedSysForm == null) {
      setCurrentSystem(null);
    }
  }, [openedSysForm]);

  useEffect(() => {
    if (isLoaded === false || isRerendering === true) {
      const fetchData = async () => {
        try {
          const response = await getSystems();
          if (response.status >= 200 && response.status < 300) {
            setSystems(response.data.Systems);
            const idsArr = response.data.Systems.map(a => a.SysId);
            const lowercaseSystemIDs = idsArr.map(id => id.toLowerCase());
            setSystemIDs(lowercaseSystemIDs);
            const namesArr = response.data.Systems.map(a => a.SystemName);
            const lowercaseSystemNames = namesArr.map(name => name.toLowerCase());
            setSystemNames(lowercaseSystemNames);
            setIsLoaded(true);
            setIsRerendering(false);
            setSystemCount(response.data.Systems.length);
          }
        } catch (err) {
          Logger.error("fetchData - Error whilte fetching Data: ", err);
        } finally {
          setIsLoaded(true);
        }
      };
      fetchData();
    }
  }, [isRerendering]);

  useEffect(() => {
    setSystemCount(systems?.length || 0);
  }, [systems]);

  const handleEditClick = rowData => {
    objAppInsights.trackEvent("systemEditClick");
    setOpenedSysForm("Edit");
    setCurrentSystem(rowData);
    systems.forEach((sys, index) => {
      if (sys.SysId === rowData.SysId) {
        setEditSystemIndex(index);
      }
    });
  };

  const handleTrashClick = rowData => {
    objAppInsights.trackEvent("systemDeleteClick");
    setDeleteSystemData(rowData);
    setIsDeleteSystemModalOpen(true);
  };

  const submitDeleteSystem = async Reason => {
    setIsDeleting(true);
    await deleteSystem({ SysId: deleteSystemData.SysId, Reason });
    const updatedSystems = systems.filter(item => item.SysId !== deleteSystemData.SysId);
    setSystems(updatedSystems);
    const systemIDsAfterDelete = systemIDs.filter(item => item !== deleteSystemData.SysId.toLowerCase());
    setSystemIDs(systemIDsAfterDelete);
    const systemNamesAfterDelete = systemNames.filter(item => item !== deleteSystemData.SystemName.toLowerCase());
    setSystemNames(systemNamesAfterDelete);
    setDeleteSystemData({});
    setIsDeleteSystemModalOpen(false);
    setIsDeleting(false);
    objAppInsights.trackEvent("systemDeleted");
  };

  const handleDownloadClick = async sysId => {
    objAppInsights.trackEvent("systemDownloadClick");
    try {
      const response = await downloadMetadata(sysId);
      if (response.status >= 200 && response.status < 300) {
        const filename = response.headers["content-disposition"].split("filename=")[1];
        const data = new Blob([response.data], { type: "text/csv" });
        const csvURL = window.URL.createObjectURL(data);
        const tempLink = document.createElement("a");
        tempLink.href = csvURL;
        tempLink.setAttribute("download", filename);
        tempLink.click();
      }
    } catch (err) {
      Logger.error("handleDownloadClick - Error whilte fetching Data: ", err);
    }
  };

  const getRowNodeId = data => data.SysId;

  const onDeleteDlgClose = async data => {
    if (data.action === "CONFIRM") {
      submitDeleteSystem(data.deleteReason);
    } else {
      setIsDeleteSystemModalOpen(false);
    }
  };

  const getDeleteDlgTitle = () => userProfileStrings.deleteSystem.replace("[%]", deleteSystemData?.SystemName);

  const closePopup = () => setPopupData(null);
  const onRowClicked = event => {
    objAppInsights.trackEvent("systemRowClick");
    const { data } = event;
    const rowData = getRowFormattedData(TABLE_COL_DEF, data);
    const title = data[SYSTEM_FIELDS.SystemName];
    const subTitle = SYSTEM_TABLE_COLUMNS.viewSystem;
    setPopupData({ title, subTitle, rowData });
  };

  return (
    <div className="setting-page-padding">
      {!isLoaded ? (
        <PageLoading />
      ) : (
        <>
          <div className="flex-between height-40 ">
            <BadgeLabel badgeDescription={landingPageStrings.systems} badgeText={systemCount} />
            <DupontButton onClick={handleOpenSystemForm}>{userProfileStrings.addSystembtn}</DupontButton>
          </div>
          <div className="flex-between-center">
            <div className="descriptionText ">{userProfileStrings.addSystem}</div>
            <div className="autoWidth">
              <Filter
                data={systems}
                setFilterValue={setFilterValue}
                showfilter={false}
                setShowTableFilterIcon={setShowTableFilterIcon}
                showTableFilterIcon={showTableFilterIcon}
              />
            </div>
          </div>

          <AgTable
            rowData={systems}
            onRowClicked={onRowClicked}
            quickFilterText={filterValue}
            columnDefs={TABLE_COL_DEF}
            getRowNodeId={getRowNodeId}
            context={{ handleTrashClick, handleEditClick, handleDownloadClick }}
            setBadgeCount={setSystemCount}
          />
          {(currentSystem || baselineDialog) && (
            <SystemForm
              setIsSystemFormOpen={setOpenedSysForm}
              type={openedSysForm}
              system={currentSystem}
              setSystem={setCurrentSystem}
              systems={systems}
              setSystems={setSystems}
              systemIDs={systemIDs}
              setSystemIDs={setSystemIDs}
              systemNames={systemNames}
              setSystemNames={setSystemNames}
              editSystemIndex={editSystemIndex}
              baselineDialog={baselineDialog}
              setBaselineDialog={setBaselineDialog}
            />
          )}
          {isDeleteSystemModalOpen && (
            <CriticalDeleteDialog
              title={getDeleteDlgTitle()}
              description={userProfileStrings.deleteSystemWarning}
              onDialogClose={onDeleteDlgClose}
              isOverlayLoader={isDeleting}
            />
          )}
          {popupData && <TableDialog popupData={popupData} closePopup={closePopup} />}
        </>
      )}
    </div>
  );
};

export default Systems;
